const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function imageGeometry(box, naturalWidth, naturalHeight, fit, position = '50% 50%') {
  if (!box.width || !box.height || !naturalWidth || !naturalHeight) return null;
  const sx = box.width / naturalWidth;
  const sy = box.height / naturalHeight;
  let scale = fit === 'cover' ? Math.max(sx, sy) : Math.min(sx, sy);
  if (fit === 'none') scale = 1;
  if (fit === 'scale-down') scale = Math.min(1, scale);
  const width = fit === 'fill' ? box.width : naturalWidth * scale;
  const height = fit === 'fill' ? box.height : naturalHeight * scale;
  const positions = position.trim().split(/\s+/);
  const offset = (value, free) => {
    if (value?.endsWith('%')) return free * parseFloat(value) / 100;
    if (value?.endsWith('px')) return parseFloat(value);
    if (value === 'left' || value === 'top') return 0;
    if (value === 'right' || value === 'bottom') return free;
    return free / 2;
  };
  const left = box.left + offset(positions[0], box.width - width);
  const top = box.top + offset(positions[1], box.height - height);
  const visibleLeft = Math.max(box.left, left);
  const visibleTop = Math.max(box.top, top);
  const visibleRight = Math.min(box.left + box.width, left + width);
  const visibleBottom = Math.min(box.top + box.height, top + height);
  return {
    left, top, width, height,
    visible: {
      left: visibleLeft, top: visibleTop,
      right: visibleRight, bottom: visibleBottom,
      width: visibleRight - visibleLeft, height: visibleBottom - visibleTop,
    },
  };
}

export function zoomWindow(image, panelWidth, panelHeight, requestedZoom, point) {
  const visible = image.visible;
  const zoom = Math.max(requestedZoom, panelWidth / visible.width, panelHeight / visible.height);
  const width = panelWidth / zoom;
  const height = panelHeight / zoom;
  const left = clamp(point.x - width / 2, visible.left, visible.right - width);
  const top = clamp(point.y - height / 2, visible.top, visible.bottom - height);
  return {
    left, top, width, height,
    backgroundWidth: image.width * zoom,
    backgroundHeight: image.height * zoom,
    backgroundX: -(left - image.left) * zoom,
    backgroundY: -(top - image.top) * zoom,
  };
}

export class ZoaeHoverMagnifier {
  constructor() {
    this.media = matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    this.active = null;
    this.frame = 0;
    this.panel = null;
    this.lens = null;
    this.hint = null;
    this.paused = false;
    this.candidate = null;
    document.addEventListener('pointerover', (event) => this.enter(event), { passive: true });
    document.addEventListener('pointermove', (event) => this.enter(event), { passive: true });
    document.addEventListener('pointerout', (event) => {
      if (this.candidate && !this.candidate.container.contains(event.relatedTarget)) this.hide();
    }, { passive: true });
    document.addEventListener('pointercancel', () => this.hide(), { passive: true });
    document.addEventListener('focusin', (event) => {
      if (event.target.matches('.product-media-container__zoom-button:focus-visible')) this.enter(event, true);
    });
    document.addEventListener('focusout', () => this.hide());
    document.addEventListener('click', () => this.hide(), true);
    document.addEventListener('keydown', (event) => {
      this.toggle(event);
    });
    document.addEventListener('scroll', () => this.hide(), { capture: true, passive: true });
    window.addEventListener('resize', () => this.hide(), { passive: true });
    window.addEventListener('blur', () => this.hide());
    this.media.addEventListener('change', () => this.hide());
    new MutationObserver((records) => {
      if (this.candidate && (!this.candidate.container.isConnected || document.querySelector('dialog[open]') || records.some((record) => record.target === this.candidate.image))) this.hide();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'src', 'srcset', 'data-max-resolution'] });
  }

  elements() {
    if (this.panel) return;
    this.panel = document.createElement('div');
    this.panel.className = 'zoae-hover-magnifier-panel';
    this.panel.hidden = true;
    this.panel.setAttribute('aria-hidden', 'true');
    this.lens = document.createElement('div');
    this.lens.className = 'zoae-hover-magnifier-lens';
    this.lens.hidden = true;
    this.lens.setAttribute('aria-hidden', 'true');
    this.hint = document.createElement('div');
    this.hint.className = 'zoae-hover-magnifier-hint';
    this.hint.hidden = true;
    this.hint.setAttribute('role', 'status');
    this.hint.setAttribute('aria-live', 'polite');
    document.body.append(this.panel, this.lens, this.hint);
  }

  toggle(event) {
    if (event.key !== 'Escape' || event.repeat || !this.media.matches || !this.candidate || document.querySelector('dialog[open]')) return;
    if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const candidate = this.candidate;
    if (!candidate.container.isConnected) return this.hide();
    this.paused = !this.paused;
    if (this.paused) {
      this.hide(false);
      this.showHint(candidate);
    } else {
      this.enter({ target: candidate.image, clientX: candidate.point.x, clientY: candidate.point.y });
    }
  }

  showHint(candidate) {
    const data = candidate.gallery.dataset;
    this.hint.textContent = this.paused ? data.zoaeMagnifierPausedText : data.zoaeMagnifierHintText;
    Object.assign(this.hint.style, {
      left: `${Math.max(12, candidate.geometry.visible.left + 12)}px`,
      top: `${Math.max(12, Math.min(candidate.geometry.visible.bottom, innerHeight - 12) - 44)}px`,
      maxWidth: `${Math.max(120, candidate.geometry.visible.width - 24)}px`,
      fontSize: `${clamp(Number(data.zoaeMagnifierHintSize) || 12, 10, 18)}px`,
    });
    this.hint.hidden = data.zoaeMagnifierHint === 'false' || !this.hint.textContent;
  }

  source(target) {
    if (!(target instanceof Element)) return null;
    const container = target.closest('.product-media-container--image');
    if (!container || container.closest('dialog, .dialog-zoomed-gallery')) return null;
    const gallery = container.closest('media-gallery[data-zoae-hover-zoom]');
    if (!gallery || target.closest('slideshow-controls, slideshow-arrows')) return null;
    const image = container.querySelector('.product-media__image');
    if (!image?.complete || !image.naturalWidth) return null;
    return { container, gallery, image };
  }

  enter(event, keyboard = false) {
    if (!this.media.matches || event.pointerType === 'touch' || document.querySelector('dialog[open]')) return this.hide();
    const source = this.source(event.target);
    if (!source) return this.hide();
    const box = source.image.getBoundingClientRect();
    if (box.width < 20 || box.height < 20) return this.hide();
    const style = getComputedStyle(source.image);
    const geometry = imageGeometry(box, source.image.naturalWidth, source.image.naturalHeight, style.objectFit, style.objectPosition);
    const containerBox = source.container.getBoundingClientRect();
    geometry.visible.left = Math.max(geometry.visible.left, containerBox.left);
    geometry.visible.top = Math.max(geometry.visible.top, containerBox.top);
    geometry.visible.right = Math.min(geometry.visible.right, containerBox.right);
    geometry.visible.bottom = Math.min(geometry.visible.bottom, containerBox.bottom);
    geometry.visible.width = geometry.visible.right - geometry.visible.left;
    geometry.visible.height = geometry.visible.bottom - geometry.visible.top;
    if (geometry.visible.width <= 0 || geometry.visible.height <= 0) return this.hide();
    const point = keyboard ? {
      x: (geometry.visible.left + geometry.visible.right) / 2,
      y: (geometry.visible.top + geometry.visible.bottom) / 2,
    } : { x: event.clientX, y: event.clientY };
    if (point.x < geometry.visible.left || point.x > geometry.visible.right || point.y < geometry.visible.top || point.y > geometry.visible.bottom) return this.hide();
    const section = source.gallery.closest('.product-information');
    const details = section?.querySelector('.product-details');
    if (!details) return this.hide();
    const detailsBox = details.getBoundingClientRect();
    const galleryBox = source.gallery.getBoundingClientRect();
    if (detailsBox.left < galleryBox.right - 2 && detailsBox.right > galleryBox.left + 2) return this.hide();
    const width = Math.min(Number(source.gallery.dataset.zoaeMagnifierWidth) || 480, detailsBox.width, innerWidth - 32);
    const header = document.querySelector('header-component');
    const headerBox = header?.getBoundingClientRect();
    const topLimit = Math.max(12, headerBox && headerBox.bottom > 0 && headerBox.top < 160 ? headerBox.bottom + 12 : 12);
    const height = Math.min(Number(source.gallery.dataset.zoaeMagnifierHeight) || 480, innerHeight - topLimit - 20);
    if (width < 160 || height < 160) return this.hide();
    const left = clamp(detailsBox.left, 16, innerWidth - width - 16);
    const top = clamp(box.top, topLimit, innerHeight - height - 16);
    this.elements();
    const url = source.image.dataset.maxResolution || source.image.currentSrc || source.image.src;
    if (this.active?.image !== source.image || this.active?.url !== url) {
      this.panel.style.backgroundImage = `url(${JSON.stringify(url)})`;
    }
    this.candidate = { ...source, geometry, point, left, top, width, height, url };
    if (this.paused) {
      this.hide(false);
      this.showHint(this.candidate);
      return;
    }
    this.active = this.candidate;
    if (!this.frame) this.frame = requestAnimationFrame(() => this.paint());
  }

  paint() {
    this.frame = 0;
    const active = this.active;
    if (!active || !active.container.isConnected) return this.hide();
    const zoom = zoomWindow(active.geometry, active.width, active.height, Number(active.gallery.dataset.zoaeMagnifierScale) || 2.5, active.point);
    Object.assign(this.panel.style, {
      left: `${active.left}px`, top: `${active.top}px`,
      width: `${active.width}px`, height: `${active.height}px`,
      backgroundSize: `${zoom.backgroundWidth}px ${zoom.backgroundHeight}px`,
      backgroundPosition: `${zoom.backgroundX}px ${zoom.backgroundY}px`,
    });
    Object.assign(this.lens.style, {
      left: `${zoom.left}px`, top: `${zoom.top}px`,
      width: `${zoom.width}px`, height: `${zoom.height}px`,
    });
    this.panel.hidden = false;
    this.lens.hidden = active.gallery.dataset.zoaeMagnifierLens === 'false';
    this.showHint(active);
  }

  hide(clearCandidate = true) {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.active = null;
    if (this.panel) this.panel.hidden = true;
    if (this.lens) this.lens.hidden = true;
    if (this.hint) this.hint.hidden = true;
    if (clearCandidate) this.candidate = null;
  }
}

if (typeof document !== 'undefined') new ZoaeHoverMagnifier();
